<template>
	<component
		:is="isXS ? 'div' : Content"
		:is-mobile="isXS"
		class="absolute height-full pointer-events-none"
		:class="isXS ? 'width-full' : ''"
	>
		<div class="relative height-full" :class="isXS ? 'width-full' : ''">
			<DialogContainer
				:is-simple="!isXS"
				:is-bottom-sheet="isXS"
				size="xs"
				class="right-0"
				:class="isXS ? 'pb-24' : 'destination-container'"
			>
				<DialogHeader closable close-filled @cancel="onClose" />
				<div class="relative">
					<div
						class="rounded-t-4 d-flex relative overflow-hidden"
						:style="{ height: destination?.isRecommended ? '150px' : '85px' }"
					>
						<RecommendedChip
							v-if="destination?.isRecommended"
							class="z-1 absolute left-0 my-4 mx-3"
							:is-x-s="isXS"
							show-text-mobile
						/>
						<Skeleton v-if="isLoading" class="width-full height-full absolute" />
						<Cover
							v-if="destination && destination.isRecommended"
							:src="destination?.cover || ''"
							:obscure="Boolean(destination?.cover)"
							width="100%"
							height="100%"
							class="absolute"
						/>
						<p
							class="text-title-l bottom-0 px-6 absolute text-neutral-25"
							:class="destination?.isRecommended ? 'text-neutral-25 py-3' : 'text-neutral-900'"
						>
							{{ destination?.name }}
						</p>
					</div>
				</div>
				<DialogBody>
					<div class="d-flex flex-column justify-center gap-3">
						<div class="d-flex gap-3 justify-center">
							<PButton
								type="toggle"
								:color="visited ? 'primary' : 'neutral'"
								:style="{ width: '100%' }"
								@click="onVisited"
								:disabled="isLoading"
							>
								<div class="d-flex align-center gap-1">
									<Icon
										:icon="visited ? 'flag-filled' : 'flag'"
										:class="
											isLoading
												? 'text-neutral-300'
												: visited
													? 'text-primary-700'
													: 'text-neutral-800'
										"
										radius="16px"
										:stroke-width="1.2"
									/>
									{{ $t('common.action.visited') }}
								</div>
							</PButton>
							<PButton
								type="toggle"
								:color="wished ? 'secondary' : 'neutral'"
								:style="{ width: '100%' }"
								@click="onWished"
								:disabled="isLoading"
							>
								<div class="d-flex align-center gap-1">
									<Icon
										:icon="wished ? 'star-filled' : 'star-outlined'"
										:class="
											isLoading
												? 'text-neutral-300'
												: wished
													? 'text-secondary-700'
													: 'text-neutral-800'
										"
										radius="16px"
										:stroke-width="1.2"
									/>
									{{ $t('common.action.wishlist') }}
								</div>
							</PButton>
						</div>
						<PButton type="filled" icon="add" @click="openCreateNewTrip" :disabled="isLoading">
							{{ $t('common.action.new_trip') }}
						</PButton>
						<a
							v-if="destination?.hasCuratedSite"
							:href="isLoading ? '' : micrositeUrl"
							@click.prevent="navigateTo(micrositeUrl)"
							class="link align-self-center text-body-m"
							:class="isLoading ? 'text-neutral-400' : 'text-neutral-800'"
						>
							{{ $t('common.action.explore_more') }}
						</a>
					</div>
				</DialogBody>
			</DialogContainer>
		</div>
	</component>
</template>

<script setup lang="ts">
	import { computed } from 'vue'
	import { Destination2 } from '~/models/Destination'
	import Content from '~/components/passporterUi/Content.vue'
	import { DestinationNavigation } from '~/services/navigation/destinationNavigation'

	interface Props {
		wished?: boolean
		visited?: boolean
		isLoading?: boolean
		destination?: Destination2
		isXS: boolean
	}
	const props = defineProps<Props>()
	const emit = defineEmits<{
		(e: 'close'): void
		(e: 'toggle-visited'): void
		(e: 'toggle-wishlist'): void
		(e: 'open-trip', id: string): void
	}>()

	const onVisited = () => emit('toggle-visited')

	const onWished = () => emit('toggle-wishlist')

	const openCreateNewTrip = () => emit('open-trip', props.destination!.id)

	const onClose = () => emit('close')

	const micrositeUrl = computed(() => {
		if (props.destination) {
			return DestinationNavigation.destinationUrl(
				props.destination.id,
				props.destination.name,
				props.destination.countryName
			)
		}
		return ''
	})
</script>

<style lang="scss">
	.destination-container {
		top: 20%;
	}

	.link {
		&:hover {
			text-decoration: underline;
		}
	}
</style>
